import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Card, CardContent, Button, Input, Alert } from '@license-management/ui';
import { LogIn, AlertCircle, Download, Key, Monitor } from 'lucide-react';

function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Nordic Navy branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0A1628] overflow-hidden">
        {/* Subtle atmospheric layers */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Fine dot grid */}
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(90,145,196,0.4) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />
          {/* Soft navy glow top-right */}
          <div className="absolute -top-20 right-0 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl animate-float" />
          {/* Teal glow bottom-left */}
          <div
            className="absolute -bottom-20 -left-10 w-80 h-80 bg-accent-500/15 rounded-full blur-3xl animate-float"
            style={{ animationDelay: '2.5s' }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full py-12 px-14 text-white">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-14">
            <img
              src="/icon.png"
              alt="Nordic Graphic Aid"
              className="h-10 w-auto object-contain"
            />
            <div>
              <h2 className="text-sm font-semibold tracking-widest uppercase text-white/90">
                Nordic Graphic Aid
              </h2>
              <p className="text-xs text-primary-400 tracking-wider">Software Portal</p>
            </div>
          </div>

          {/* Headline */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold leading-tight mb-4 tracking-tight">
              Your software,<br />
              <span className="text-primary-300">always current.</span>
            </h1>
            <p className="text-base text-[#7a9ab8] leading-relaxed max-w-xs">
              Download the latest version, manage your license, and track device activations.
            </p>
          </div>

          {/* Feature list */}
          <div className="space-y-4">
            {[
              { icon: Download, title: 'Downloads', desc: 'Get the latest version instantly' },
              { icon: Key,      title: 'License',   desc: 'View and manage your license key' },
              { icon: Monitor,  title: 'Devices',   desc: 'Track and manage activations' },
            ].map((feature, i) => (
              <div
                key={feature.title}
                className="flex items-center gap-4 animate-slide-in-from-left"
                style={{ animationDelay: `${i * 80 + 150}ms` }}
              >
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-500/20 border border-primary-400/20 flex-shrink-0">
                  <feature.icon className="h-4 w-4 text-primary-300" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{feature.title}</p>
                  <p className="text-xs text-[#7a9ab8]">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mascot */}
          <div className="flex-1 flex items-end justify-center pb-4 relative">
            <div className="pointer-events-none absolute inset-0 flex items-end justify-center">
              <div
                className="w-72 h-72 rounded-full opacity-30"
                style={{
                  background: 'radial-gradient(ellipse, rgba(61,100,147,0.6) 0%, rgba(47,160,178,0.2) 40%, transparent 70%)',
                  filter: 'blur(50px)',
                  transform: 'translateY(30%)',
                }}
              />
            </div>
            <div
              className="relative z-10"
              style={{ animation: 'mascotFloat 5s ease-in-out infinite' }}
            >
              <div
                className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-28 h-5 rounded-full opacity-25"
                style={{
                  background: 'radial-gradient(ellipse, rgba(61,100,147,0.8) 0%, transparent 70%)',
                  filter: 'blur(6px)',
                }}
              />
              <img
                src="/icon.png"
                alt=""
                aria-hidden="true"
                className="relative h-56 w-auto object-contain"
                style={{
                  filter: 'drop-shadow(0 0 30px rgba(61,100,147,0.5)) drop-shadow(0 16px 32px rgba(0,0,0,0.5))',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex items-center justify-center bg-[#F9F8F5] py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {/* Mobile logo */}
          <div className="text-center mb-8 lg:hidden">
            <img
              src="/icon.png"
              alt="Nordic Graphic Aid"
              className="h-20 w-auto mx-auto object-contain mb-4"
              style={{ animation: 'mascotFloat 5s ease-in-out infinite' }}
            />
            <h1 className="text-2xl font-bold text-[#1A2230]">Software Portal</h1>
            <p className="mt-1 text-sm text-[#6B7587]">Nordic Graphic Aid</p>
          </div>

          {/* Desktop header */}
          <div className="hidden lg:block mb-8">
            <h1 className="text-2xl font-bold text-[#1A2230] mb-1 tracking-tight">
              Welcome back
            </h1>
            <p className="text-[#6B7587] text-sm">
              Sign in to access your software and licenses
            </p>
          </div>

          {/* Login card */}
          <Card shadow="elevated" className="animate-fade-in">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <Alert variant="error" icon={<AlertCircle className="h-5 w-5" />}>
                    {error}
                  </Alert>
                )}

                <Input
                  label="Email address"
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />

                <Input
                  label="Password"
                  type="password"
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                />

                <Button
                  type="submit"
                  loading={isLoading}
                  className="w-full"
                  size="lg"
                  leftIcon={!isLoading ? <LogIn className="h-4 w-4" /> : undefined}
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="mt-6 text-center text-sm text-[#A8ADB8]">
            Need access?{' '}
            <span className="text-primary-600 font-medium cursor-default">Contact support</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
